<template>
  <div v-if="appId" class="tw-w-[250px] tw-mx-auto tw-mb-[20px]">
    <div
      id="facebook-signin-button"
      ref="facebookButton"
      class="fb-login-button"
      data-size="large"
      data-button-type="continue_with"
      data-layout="default"
      data-auto-logout-link="false"
      data-use-continue-as="false"
      data-scope="public_profile,email"
      data-onlogin="charidyOnFacebookLogin"
    ></div>
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";

// i18n key + English fallback for the user-visible error shown in the login form when the
// Facebook OAuth round-trip fails on the SDK / browser side — i.e. before we ever get a
// token to send to our backend. Examples: SDK script blocked by an ad-blocker or CSP,
// FB.init throws, the App ID points at an inactive/misconfigured Facebook App so the user
// dismisses the FB dialog after seeing FB's own error screen, or the user declines to
// grant the requested email/public_profile scopes. Surfaced via the existing setErrorLogin
// Vuex mutation so it renders in the same `<error>` slot as password-login errors.
const FACEBOOK_LOGIN_ERROR_KEY = "template.facebook_login_failed";
const FACEBOOK_LOGIN_ERROR_FALLBACK =
  "Facebook sign-in failed. Please try again, or contact support if the problem persists.";

export default {
  name: "FacebookLogin",

  computed: {
    ...mapState({
      appId: (state) => state.account.facebookAppId,
      // Version comes from /initial (backend charidy_credentials.meta.graph_api_version);
      // the store already falls back to "v20.0" when the field is absent.
      graphApiVersion: (state) => state.account.facebookGraphApiVersion || "v20.0",
    }),
  },

  watch: {
    appId(newValue) {
      if (newValue) {
        this.initFacebookAuth();
      }
    },
  },

  methods: {
    ...mapMutations(["setErrorLogin"]),

    showLoginError(detail) {
      console.warn("[facebook-login]", detail);
      this.setErrorLogin([FACEBOOK_LOGIN_ERROR_KEY, FACEBOOK_LOGIN_ERROR_FALLBACK]);
    },

    loginUser(accessToken) {
      this.$store
        .dispatch("loginUser", [accessToken, { noRouting: true, facebookLogin: true }])
        .then((user) => {
          this.$emit("onLoginSuccess", user);
        })
        .catch((error) => {
          this.$emit("onLoginError", error);
        });
    },

    handleFacebookLoginStateChange() {
      if (!window.FB) {
        this.showLoginError("FB SDK not available when onlogin callback fired");
        return;
      }

      window.FB.getLoginStatus((response) => {
        const accessToken = response?.authResponse?.accessToken;
        if (response?.status === "connected" && accessToken) {
          this.setErrorLogin([]);
          this.loginUser(accessToken);
          return;
        }

        // Anything other than `connected` after the user clicked the FB button means the
        // OAuth round-trip failed end-to-end. Common causes:
        //   * status "unknown"        — FB App is inactive, App Domain misconfigured,
        //                               browser blocked the SDK, or the user closed the
        //                               dialog after seeing FB's own error screen.
        //   * status "not_authorized" — user is logged into Facebook but declined to grant
        //                               our app the requested scopes (email/public_profile).
        // The SDK doesn't give us a structured reason, so we surface a single generic
        // message and log the raw response for ops debugging.
        this.showLoginError(
          `FB.getLoginStatus returned non-connected: ${JSON.stringify(response)}`
        );
      });
    },

    initFacebookAuth() {
      if (!this.appId) return;

      window.charidyOnFacebookLogin = () => this.handleFacebookLoginStateChange();

      const runInit = () => {
        try {
          window.FB.init({
            appId: this.appId,
            cookie: true,
            xfbml: true,
            version: this.graphApiVersion,
          });
        } catch (err) {
          this.showLoginError(`FB.init threw: ${err?.message || err}`);
          return;
        }

        this.$nextTick(() => {
          if (window.FB?.XFBML && this.$refs.facebookButton) {
            window.FB.XFBML.parse(this.$refs.facebookButton.parentNode);
          }
        });
      };

      if (window.FB) {
        runInit();
        return;
      }

      const existingScript = document.getElementById("facebook-jssdk");
      if (existingScript) {
        window.fbAsyncInit = runInit;
        return;
      }

      window.fbAsyncInit = runInit;

      const facebookScript = document.createElement("script");
      facebookScript.id = "facebook-jssdk";
      facebookScript.src = "https://connect.facebook.net/en_US/sdk.js";
      facebookScript.async = true;
      facebookScript.defer = true;
      facebookScript.onerror = () => {
        this.showLoginError(
          "Failed to load https://connect.facebook.net/en_US/sdk.js — likely blocked by a " +
            "tracking-protection extension, ad-blocker, or restrictive CSP."
        );
      };
      document.head.appendChild(facebookScript);
    },
  },

  mounted() {
    if (this.appId) {
      this.initFacebookAuth();
    }
  },

  beforeDestroy() {
    if (window.charidyOnFacebookLogin) {
      delete window.charidyOnFacebookLogin;
    }
  },
};
</script>
